/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import './index.less';

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  LinkButton,
  Menu,
  MenuItem,
  MenuList,
  MenuToggle,
  MenuToggleIcon,
  OverflowTooltip,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardServicesStore } from 'src/models/useDashboardServicesModel';
import { ServiceNameEnum, ServiceType } from '@Types/index';
import xor from 'lodash/xor';

import { useLicenseStore } from '../../../models/useLicenseModel';
import { useOrgStore } from '../../../models/useOrgModel';
import {
  getServiceNamesMapping,
  isServiceName,
  SERVICE_TYPES,
} from '../../../utils/service';
import { reportPendo } from '../../../utils/setupPendo';
import { useGlobalSettingStore } from '../../../models/useGlobalSettingModel';
import { useProvisionedServicesQuery } from '@Dashboard/ThreatDetection/useProvisionedServicesQuery';

const ServiceCheckbox = (props: { includeServices?: Set<ServiceNameEnum> }) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const servicesModel = useDashboardServicesStore();
  const licenseModule = useLicenseStore();
  const { selectedOrgGlobalSetting, isGlobalSettingLoaded } =
    useGlobalSettingStore();
  const { orgId } = useOrgStore();
  const [provisionedServices, setProvisionedServices] = useState<
    ServiceNameEnum[]
  >([]);
  const [servicesMaps, setServicesMaps] = useState<ServiceType[]>([]);

  const getExcludeServices = useCallback(() => {
    const excludeServices = new Set<ServiceNameEnum>([
      ServiceNameEnum.MicrosoftIdentityProtection,
      ServiceNameEnum.MicrosoftInformationProtection,
    ]);
    if (!selectedOrgGlobalSetting.smexHybridFeatureShow) {
      excludeServices.add(ServiceNameEnum.ExchangeServer);
    } else if (!selectedOrgGlobalSetting.gmailInlineFeatureShow) {
      excludeServices.add(ServiceNameEnum.GmailInline);
    }
    if (
      !licenseModule.license['CAS-SF'] ||
      licenseModule.license['CAS-SF'].enabled === false
    ) {
      excludeServices.add(ServiceNameEnum.SalesforceSandbox);
      excludeServices.add(ServiceNameEnum.SalesforceProduction);
    }
    if (!selectedOrgGlobalSetting.exchangeInlineFeatureShow) {
      excludeServices.add(ServiceNameEnum.ExchangeInline);
    }
    return excludeServices;
  }, [selectedOrgGlobalSetting, licenseModule]);

  const serviceTypes = useMemo(() => {
    return SERVICE_TYPES.filter((x) => !getExcludeServices().has(x.name))
      .filter(
        (x) => !props.includeServices || props.includeServices?.has(x.name)
      )
      .sort((a, b) => a.order - b.order);
  }, [getExcludeServices, props.includeServices]);

  const initCurSelectedServices = useCallback(
    (selectedServiceNames: ServiceNameEnum[]) => {
      servicesModel.setAllServicesSelectedStatus(selectedServiceNames, true);
      setServicesMaps(serviceTypes.slice().sort((a, b) => a.order - b.order));
    },
    [serviceTypes, servicesModel]
  );

  const loadLocalStorageServices = useCallback(
    (curSelectedServices: any) => {
      const selectedServiceNames: ServiceNameEnum[] = [];
      for (const [key, value] of Object.entries(
        curSelectedServices as { [key in ServiceNameEnum]: boolean }
      )) {
        if (
          value &&
          isServiceName(key) &&
          provisionedServices.includes(key as ServiceNameEnum)
        ) {
          selectedServiceNames.push(key as ServiceNameEnum);
        }
      }
      initCurSelectedServices(selectedServiceNames);
    },
    [initCurSelectedServices, provisionedServices]
  );

  const handleChange = (serviceName: ServiceNameEnum, checked: boolean) => {
    reportPendo('Dashboard.SelectedServices.Checked', {
      service: serviceName,
      checked,
    });
    servicesModel.updateServiceSelectedStatus(serviceName, checked);
  };

  const { data: provisionedServiceTypeList } =
    useProvisionedServicesQuery(orgId);

  useEffect(() => {
    if (provisionedServiceTypeList && isGlobalSettingLoaded) {
      const provisionedServiceNames = getServiceNamesMapping(
        serviceTypes,
        provisionedServiceTypeList
      );
      setProvisionedServices(provisionedServiceNames);
    }
  }, [provisionedServiceTypeList, serviceTypes, isGlobalSettingLoaded]);

  useEffect(() => {
    if (!isGlobalSettingLoaded) return;
    const curSelectedServices = localStorage.getItem('casCurSelectedServices')
      ? JSON.parse(localStorage.getItem('casCurSelectedServices') as string)
      : {};
    if (curSelectedServices && Object.keys(curSelectedServices).length > 0) {
      loadLocalStorageServices(curSelectedServices);
    } else {
      const casCurSelectedServices: { [key: string]: boolean } = {};
      provisionedServices.forEach((item: string) => {
        casCurSelectedServices[item] = true;
      });
      initCurSelectedServices(provisionedServices);
    }
  }, [provisionedServices, isGlobalSettingLoaded]);

  return (
    <Flex align="center">
      <Box
        as="span"
        position="static"
        fontSize="14px"
        lineHeight="25px"
        mr="16px"
        color={colorStyle.color.secondary}
      >
        {t('dashboard.services')}:
      </Box>
      <Menu closeOnSelect={false}>
        <MenuToggle>
          {({ getMenuToggleProps }: any) => {
            return (
              <Button
                {...getMenuToggleProps()}
                variant="secondary"
                minWidth={195}
                justifyContent="space-between"
              >
                <Text size="sm" style={{ marginLeft: '4px' }}>
                  {t('common.label.selectedServices')} (
                  {servicesModel.enableCount})
                </Text>
                <Space width="1x" />
                <MenuToggleIcon />
              </Button>
            );
          }}
        </MenuToggle>
        <MenuList>
          <ServiceCheckboxGroup
            provisionedServices={provisionedServices}
            servicesMaps={servicesMaps}
            onChange={handleChange}
          />
        </MenuList>
      </Menu>
    </Flex>
  );
};
export default ServiceCheckbox;

function ServiceCheckboxGroup(props: {
  provisionedServices: ServiceNameEnum[];
  servicesMaps: ServiceType[];
  onChange: (service: ServiceNameEnum, checked: boolean) => void;
}) {
  const { provisionedServices, servicesMaps, onChange } = props;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const servicesModel = useDashboardServicesStore();

  const [value, setValue] = useState<ServiceNameEnum[]>(() =>
    provisionedServices.filter((name) => servicesModel.services[name])
  );

  const handleChange = (serviceNames: ServiceNameEnum[]) => {
    const checked = serviceNames.length > value.length;
    const updatedServices = xor(value, serviceNames);
    if (updatedServices[0]) onChange(updatedServices[0], checked);
    setValue(serviceNames);
  };

  const isAllSelected = value.length === provisionedServices.length;
  const handleClickToggle = () => {
    const nextValue = isAllSelected ? [] : provisionedServices;
    servicesModel.setAllServicesSelectedStatus(
      provisionedServices,
      !isAllSelected
    );

    // Convert array to object format expected by localStorage
    const casCurSelectedServices: { [key: string]: boolean } = {};
    provisionedServices.forEach((service) => {
      casCurSelectedServices[service] = nextValue.includes(service);
    });
    localStorage.setItem(
      'casCurSelectedServices',
      JSON.stringify(casCurSelectedServices)
    );
    setValue(nextValue);
  };

  return (
    <CheckboxGroup
      name="services"
      value={value}
      defaultValue={value}
      onChange={handleChange}
      size="sm"
    >
      <Box px="3x" mb="2x">
        <LinkButton key="toggle" onClick={handleClickToggle}>
          {isAllSelected
            ? t('common.service.clearAll')
            : t('common.service.selectAll')}
        </LinkButton>
      </Box>
      {servicesMaps.map((e: ServiceType) => {
        const disabled = provisionedServices.indexOf(e.name) === -1;
        const color = disabled
          ? colorStyle.color.disabled
          : colorStyle.color.primary;
        const isChecked = value.includes(e.name);

        const handleItemClick = () => {
          if (!disabled) {
            const newValue = isChecked
              ? value.filter((v) => v !== e.name)
              : [...value, e.name];
            handleChange(newValue);
          }
        };

        return (
          <MenuItem
            key={e.name}
            lineHeight="20px"
            width="195px"
            color={color}
            onClick={handleItemClick}
            cursor={disabled ? 'not-allowed' : 'pointer'}
          >
            <Checkbox
              pr="8px"
              value={e.name}
              key={e.name}
              disabled={disabled}
              checked={isChecked}
              readOnly
              pointerEvents="none"
            />
            <OverflowTooltip
              PopperProps={{ usePortal: true }}
              label={t(`common.service.${e.name}`)}
            >
              {t(`common.service.${e.name}`)}
            </OverflowTooltip>
          </MenuItem>
        );
      })}
    </CheckboxGroup>
  );
}
